import { SCORE_DECIMAL_PLACES } from '../calibrationConstants';

import { toReaderLocale } from '@/utils/numberFormat';

/** `Intl.NumberFormat` renders a negative sign as a plain hyphen-minus; the deviation charts and
 * table want the typographic minus sign instead. */
const TYPOGRAPHIC_MINUS_SIGN = '−';

/** Half of the last decimal shown: anything smaller reads as zero on screen, whatever its sign. */
const ZERO_DISPLAY_THRESHOLD = 0.5 * 10 ** -SCORE_DECIMAL_PLACES;

export interface FormatSignedPointsOptions {
    value: number;
    language: string;
}

/**
 * Formats a calibration deviation or gap as an explicitly signed, one-decimal number in the given
 * language: `+3,2` / `−3,2` (fr), `+3.2` / `−3.2` (en). A value that reads as zero at that
 * precision always renders as `+0,0`, never `−0,0`.
 */
export const formatSignedPoints = ({ value, language }: FormatSignedPointsOptions): string => {
    const normalizedValue = Math.abs(value) < ZERO_DISPLAY_THRESHOLD ? 0 : value;

    const formatted = new Intl.NumberFormat(toReaderLocale(language), {
        signDisplay: 'always',
        minimumFractionDigits: SCORE_DECIMAL_PLACES,
        maximumFractionDigits: SCORE_DECIMAL_PLACES,
    }).format(normalizedValue);

    return formatted.startsWith('-') ? `${TYPOGRAPHIC_MINUS_SIGN}${formatted.slice(1)}` : formatted;
};
